import React, { useEffect, useRef, useState } from 'react';
import { pick, prop } from 'ramda';

import { Jackpot } from '@interlayer/js/app/components/common/Jackpot';

import { SvgIcon } from 'js/app/components/common/SvgIcon';
import { GameBands } from 'js/app/components/main/Games/components/GameTile/components/GameBands';
import { GameSashes } from 'js/app/components/main/Games/components/GameTile/components/GameSashes';
import { Button } from 'js/components/common/Button';

import { lowerFirstLetter } from '@interlayer/js/app/utils/common.util';

import { GAME_EVENTS } from 'js/app/components/main/Games/constants';
import { useGameComponentConfig } from 'js/app/components/main/Games/hooks/useGameComponentConfig';
import { useGameEventDispatch } from 'js/app/components/main/Games/hooks/useGameEventDispatch';
import { useGameInfo } from 'js/app/components/main/Games/hooks/useGameInfo';
import { useAutoGameLaunch } from 'js/app/components/main/Games/hooks/useGameLaunch';
import { setGameListInStorage } from 'js/app/components/popups/GamesInfoPopup/utils';
import { useInMobile } from 'js/app/hooks/device/hooks/useMediaQuery';
import { useOnScreen } from 'js/app/hooks/device/hooks/useOnScreen';
import { useTranslations } from 'js/hooks/i18n';
import { useAppSelector, useUserSelector } from 'js/store/hooks';

import { useOnFavouriteClick } from '../DetailedTile/hooks/useOnFavouriteClick';

import style from './ListTile.scss';

export const ListTile = ({
    index,
    gameCodes,
    gameData,
    isShowJackpots,
    isShowFavourites,
    withBands,
    categoryName,
    isShowSashes = true
}) => {
    const isMobile = useInMobile();
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const gameTileRef = useRef(null);
    const launchGame = useAutoGameLaunch(gameData);
    const gameEventDispatch = useGameEventDispatch();
    const [isShownOnce, setShownOnce] = useState(false);
    const { isFavoritesFetching, favoriteGames } = useAppSelector(pick(['isFavoritesFetching', 'favoriteGames']));
    const translate = useTranslations('playtech.mobile.system.buzzGame.list.');

    const {
        showBands = withBands,
        showJackpots = isShowJackpots,
        showFavourites = isShowFavourites
    } = useGameComponentConfig();

    const isOnScreen = useOnScreen({ ref: gameTileRef });

    useEffect(() => {
        if (!isShownOnce && isOnScreen) {
            setShownOnce(true);
        }
    }, [isOnScreen, isShownOnce]);

    const openGameInfo = useGameInfo(gameData);
    const handleShowInfoClick = (e) => {
        setGameListInStorage(gameCodes);
        e.stopPropagation();
        openGameInfo();
    };

    const imageSelector = lowerFirstLetter(`${!isLoggedIn ? 'Guest' : ''}${isMobile ? 'Mobile' : ''}ImageURLs`);

    const backgroundImage = isShownOnce
        ? `url(${gameData?.[imageSelector]?.c1x1})`
        : 'none';

    const isFavorite = isLoggedIn && favoriteGames.includes(gameData.gameCode);
    const handleFavouriteClick = useOnFavouriteClick({ code: gameData.gameCode, isFavorite });

    const handlerLaunchGame = () => {
        launchGame();

        if (index) {
            gameEventDispatch({
                name: GAME_EVENTS.GAME_POSITION,
                data: {
                    categoryName,
                    game: gameData.name,
                    gameCode: gameData.gameCode,
                    position: index + 1
                }
            });
        }
    };

    return (
        <div
            ref={gameTileRef}
            className={style.gameTile}
            data-game-code={gameData.gameCode}
        >
            <div className={style.imgWrapper}>
                <div
                    className={style.img}
                    style={{ backgroundImage }}
                    onClick={handlerLaunchGame}
                />

                {isShowSashes && (
                    <GameSashes additionalAssets={gameData.additionalAssets || []} />
                )}

                {showBands && (
                    <GameBands
                        gameData={gameData}
                        viewStyle="detailed"
                        isDiamondEnabled
                    />
                )}
            </div>

            <div className={style.title}>{gameData.name}</div>

            {showJackpots && (
                <div className={style.jackpotWrapper}>
                    <Jackpot
                        className={style.jackpot}
                        gameCode={gameData.gameCode}
                    />
                </div>
            )}

            <div className={style.ctaWrapper}>
                {showFavourites && isLoggedIn && !isFavoritesFetching && (
                    <SvgIcon
                        type={isFavorite ? 'favourite-filled' : 'favourite'}
                        className={style.infoFavourite}
                        onClick={handleFavouriteClick}
                    />
                )}

                <SvgIcon
                    type="info-filled"
                    className={style.infoIcon}
                    onClick={handleShowInfoClick}
                />

                <Button
                    size="normal"
                    className={style.playButton}
                    onClick={handlerLaunchGame}
                >
                    {translate('cta')}
                </Button>
            </div>
        </div>
    );
};
